import type { FormEvent } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Button from '../../../components/buttons/Button';
import { DialogContent } from '../../../components/Dialog/DialogContent';
import { DialogHeader } from '../../../components/Dialog/DialogHeader';
import { DialogLoader } from '../../../components/Dialog/DialogLoader';
import { ErrorMessage } from '../../../components/ErrorMessage/ErrorMessage';
import { FormInput } from '../../../components/FormInput/FormInput';
import { PasswordInput } from '../../../components/FormInput/PasswordInput';
import type { WizardChildProps } from '../../../components/Wizard/types';
import { analytics } from '../../../services/analytics';
import { useStores } from '../../../stores/useStores';
import { useOpenAuthWizard } from '../useOpenAuthWizard';
import { useSignUpWizardContext } from '../wizards/signUpWizard.provider';
import { useHandleSignUpWizard } from '../wizards/useHandleSIgnUpWizard';

import { getEmailErrorMessageMap, getPasswordErrorMessageMap } from './constants';
import styles from './SignUpRegister.module.scss';

interface SignUpForm extends HTMLFormElement {
  elements: HTMLFormControlsCollection & {
    email: HTMLInputElement;
    password: HTMLInputElement;
  };
}

export const SignUpRegister = observer((props: WizardChildProps) => {
  const { authStore, portalStore } = useStores();
  const { t } = useTranslation();

  const { openAuthWizard } = useOpenAuthWizard();

  const { isSigningUp } = authStore;
  const isDirectLanding = authStore.isDirectLanding();

  const { handleSignUpWizard } = useHandleSignUpWizard();

  const { error, updateContextData } = useSignUpWizardContext();
  const clearError = () => updateContextData({ error: '' });

  const openLogIn = () => {
    void analytics.sendEvent('login_account_button', {
      event_category: 'sign_up',
      event_action: 'click',
      button_text: 'log in',
    });

    openAuthWizard('logInWizard', {
      triggeredBy: 'sign_up_platform',
      force: true,
      persistent: isDirectLanding,
    });
  };

  const handleSubmit = (event: FormEvent<SignUpForm>) => {
    event.preventDefault();
    const { email, password } = event.currentTarget.elements;

    handleSignUpWizard({ provider: 'email', credentials: { email: email.value, password: password.value } });
  };

  const handlePreviousStep = () => {
    props.prevStep();
  };

  return (
    <>
      <DialogHeader title={t('auth.createAccount')} onClose={portalStore.closePortal} onGoBack={handlePreviousStep} />

      <DialogLoader isProcessing={isSigningUp} />

      <DialogContent>
        <form onChange={clearError} onSubmit={handleSubmit}>
          <div className={styles.formRow}>
            <label className="form-field__label" htmlFor="email">
              {t('common.email')}*
            </label>
            <FormInput
              required
              autoComplete="email"
              dataTestId="signup-email-input-field"
              disabled={isSigningUp}
              errorMessageMap={getEmailErrorMessageMap(t)}
              id="email"
              name="email"
              placeholder={t('forms.emailAddress')}
              type="email"
            />
          </div>

          <div className={styles.formRow}>
            <label className="form-field__label" htmlFor="password">
              {t('forms.password')}*
            </label>
            <PasswordInput
              required
              disabled={isSigningUp}
              errorMessageMap={getPasswordErrorMessageMap(t)}
              id="password"
              minLength={6}
              name="password"
              placeholder={t('auth.enterPassword')}
            />
          </div>

          <Button
            btnType="main"
            className={styles.createButton}
            dataTestId="signup-create-account-btn"
            disabled={isSigningUp}
            type="submit"
          >
            {t('auth.createAccount')}
          </Button>

          <ErrorMessage>{error}</ErrorMessage>

          <div className="animation-slide-down form-links">
            <span>{t('auth.alreadyHaveAccountQuestion')}</span>
            <span className="text-link" onClick={openLogIn}>
              {t('auth.logIn')}
            </span>
          </div>
        </form>
      </DialogContent>
    </>
  );
});
